import React, { useEffect, useMemo, useState } from 'react'
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Divider,
  FormControlLabel,
  IconButton,
  Paper,
  Stack,
  Switch,
  Tab,
  Tabs,
  TextField,
  Typography,
} from '@mui/material'
import { Add, Delete, Save } from '@mui/icons-material'
import { InspectionChecklistQuestion } from '@otw/models'
import { useSnackbar } from '@/hooks/common/useSnackbar'
import {
  DEFAULT_CHECKLIST_DOC_ID,
  DEFAULT_INTAKE_CHECKLIST_DOC_ID,
  InspectionChecklistKind,
  useInspectionChecklist,
} from '@/hooks/repair/useInspectionChecklist'
import { ConfirmDialog } from '@/components/ConfirmDialog'
import { StatusChip } from 'components/StatusChip'

const createQuestion = (order: number): InspectionChecklistQuestion => ({
  id: `${Date.now()}-${Math.random().toString(36).slice(2, 8)}`,
  question: '',
  enabled: true,
  allowNotes: true,
  order,
  answerOptions: ['yes', 'no', 'na'],
})

const normalizeQuestions = (questions: InspectionChecklistQuestion[]): InspectionChecklistQuestion[] =>
  questions.map((question, index) => ({
    ...question,
    order: index,
    answerOptions: ['yes', 'no', 'na'],
    allowNotes: true,
  }))

const kindLabel = (kind: InspectionChecklistKind) =>
  kind === 'intake' ? 'Intake' : 'Complete'

const InspectionChecklistEditor: React.FC<{ kind: InspectionChecklistKind }> = ({ kind }) => {
  const { showSuccess, showError } = useSnackbar()
  const checklistId =
    kind === 'intake' ? DEFAULT_INTAKE_CHECKLIST_DOC_ID : DEFAULT_CHECKLIST_DOC_ID
  const { checklist, questions, isLoading, isSaving, saveChecklist, error } = useInspectionChecklist({
    checklistId,
    kind,
    fallbackToDefault: false,
  })
  const [draftQuestions, setDraftQuestions] = useState<InspectionChecklistQuestion[]>([])
  const [pendingDeleteQuestion, setPendingDeleteQuestion] = useState<InspectionChecklistQuestion | null>(
    null
  )

  useEffect(() => {
    setDraftQuestions(questions)
  }, [questions])

  const hasUnsavedChanges = useMemo(() => {
    return JSON.stringify(normalizeQuestions(draftQuestions)) !== JSON.stringify(normalizeQuestions(questions))
  }, [draftQuestions, questions])

  const handleAddQuestion = () => {
    setDraftQuestions((prev) => [...prev, createQuestion(prev.length)])
  }

  const handleDeleteQuestion = (questionId: string) => {
    setDraftQuestions((prev) => normalizeQuestions(prev.filter((question) => question.id !== questionId)))
  }

  const handleQuestionChange = (questionId: string, value: string) => {
    setDraftQuestions((prev) =>
      prev.map((question) => (question.id === questionId ? { ...question, question: value } : question))
    )
  }

  const handleEnabledChange = (questionId: string, enabled: boolean) => {
    setDraftQuestions((prev) =>
      prev.map((question) => (question.id === questionId ? { ...question, enabled } : question))
    )
  }

  const handleSave = async () => {
    const normalized = normalizeQuestions(draftQuestions)
    const hasEmptyQuestion = normalized.some((question) => !question.question.trim())
    if (hasEmptyQuestion) {
      showError('Please fill out all question text before saving.')
      return
    }

    const label = kindLabel(kind)
    try {
      await saveChecklist({
        name: checklist.name || `Default ${label} Checklist`,
        questions: normalized,
        kind,
      })
      showSuccess(`${label} checklist saved successfully`)
    } catch (saveError) {
      console.error(`Error saving ${label.toLowerCase()} checklist:`, saveError)
      showError(`Failed to save ${label.toLowerCase()} checklist`)
    }
  }

  if (isLoading) {
    return (
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', p: 4 }}>
        <CircularProgress size={28} />
      </Box>
    )
  }

  const label = kindLabel(kind)

  return (
    <Stack spacing={2}>
      {(error || !checklist.id) && (
        <Alert severity="warning">
          {error ||
            `${label} checklist is not yet configured. Add questions below and save to create the default document.`}
        </Alert>
      )}

      <Stack spacing={1.5}>
        {draftQuestions.length === 0 ? (
          <Paper variant="outlined" sx={{ p: 2 }}>
            <Typography variant="body2" color="text.secondary">
              No {label.toLowerCase()} checklist questions yet. Add your first question to get started.
            </Typography>
          </Paper>
        ) : (
          draftQuestions.map((question, index) => (
            <Paper key={question.id} variant="outlined" sx={{ p: 2 }}>
              <Stack spacing={1.5}>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 1 }}>
                  <Typography variant="subtitle2">Question {index + 1}</Typography>
                  <IconButton
                    color="error"
                    size="small"
                    onClick={() => setPendingDeleteQuestion(question)}
                    aria-label={`Delete question ${index + 1}`}
                  >
                    <Delete fontSize="small" />
                  </IconButton>
                </Box>

                <TextField
                  fullWidth
                  label="Question"
                  placeholder={
                    kind === 'intake'
                      ? 'Example: Is the truck damaged on arrival?'
                      : 'Example: Are there any visible fluid leaks?'
                  }
                  value={question.question}
                  onChange={(event) => handleQuestionChange(question.id, event.target.value)}
                />

                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                  <StatusChip dense tone="success" label="Yes" />
                  <StatusChip dense tone="danger" label="No" />
                  <StatusChip dense tone="neutral" label="N/A" />
                  <StatusChip dense tone="info" label="Optional notes" />
                </Box>

                <Divider />

                <FormControlLabel
                  control={
                    <Switch
                      checked={question.enabled}
                      onChange={(event) => handleEnabledChange(question.id, event.target.checked)}
                    />
                  }
                  label={question.enabled ? 'Enabled' : 'Disabled'}
                />
              </Stack>
            </Paper>
          ))
        )}
      </Stack>

      <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
        <Button variant="outlined" startIcon={<Add />} onClick={handleAddQuestion}>
          Add Question
        </Button>
        <Button
          variant="contained"
          startIcon={<Save />}
          disabled={isSaving || !hasUnsavedChanges}
          onClick={handleSave}
        >
          {isSaving ? 'Saving...' : `Save ${label} Checklist`}
        </Button>
      </Box>
      <ConfirmDialog
        open={pendingDeleteQuestion != null}
        title="Delete checklist question?"
        message={`Are you sure you want to delete "${
          pendingDeleteQuestion?.question?.trim() || 'this question'
        }"? This action cannot be undone.`}
        confirmText="Delete question"
        cancelText="Cancel"
        severity="error"
        onCancel={() => setPendingDeleteQuestion(null)}
        onConfirm={() => {
          if (!pendingDeleteQuestion) return
          handleDeleteQuestion(pendingDeleteQuestion.id)
          setPendingDeleteQuestion(null)
        }}
      />
    </Stack>
  )
}

const InspectionChecklistTab: React.FC = () => {
  const [activeKind, setActiveKind] = useState<InspectionChecklistKind>('complete')

  return (
    <Stack spacing={2} sx={{ p: 2 }}>
      <Paper variant="outlined" sx={{ p: 2 }}>
        <Stack spacing={1}>
          <Typography variant="h6">Inspection Checklist</Typography>
          <Typography variant="body2" color="text.secondary">
            Configure the default repair checklists used when a shop does not have its own template. Every question
            supports responses: Yes, No, or N/A, with optional notes.
          </Typography>
        </Stack>
      </Paper>

      <Tabs
        value={activeKind}
        onChange={(_, value: InspectionChecklistKind) => setActiveKind(value)}
        sx={{ borderBottom: 1, borderColor: 'divider' }}
      >
        <Tab value="complete" label="Complete checklist" />
        <Tab value="intake" label="Intake checklist" />
      </Tabs>

      <InspectionChecklistEditor key={activeKind} kind={activeKind} />
    </Stack>
  )
}

export default InspectionChecklistTab
